import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthStore } from 'src/app/calvin-emr-shared/state/auth/auth.store';
import { PATH_INVALID_CABINET } from 'src/app/global';
import { PATH_LOGIN } from 'src/app/login/login.global';
import {
  commitKnightmedsInstanceId,
  getPendingKnightmedsInstanceId,
  tryResolveKnightmedsInstanceId,
} from 'src/environments/runtime-config';
import { KnightmedsInstancesService } from '../services/knightmeds-instances.service';

/** Why a cabinet id was rejected, surfaced to InvalidCabinetPageComponent */
export type InvalidCabinetReason = 'missing' | 'unknown' | 'unverifiable';

/**
 * The id already checked against Firestore during this page load. Skips a
 * redundant read on every navigation between the release/reload pages, which
 * would otherwise each cost a round trip to say what we just confirmed.
 */
let validatedInstanceId: string | undefined;

/**
 * Guards the Knightmeds pages on this laptop being a cabinet the
 * administration has actually provisioned.
 *
 * The id comes from `?cabinet=` (captured at startup), from localStorage, or
 * from the Docker KNIGHTMEDS_INSTANCE_ID variable -- see
 * src/environments/runtime-config.ts. Whichever it is, it is confirmed
 * against a knightmedsInstances/{id} document before the pages that read and
 * write that cabinet's inventory are allowed to load. A typo'd id therefore
 * lands on an error page naming the mistake, instead of silently accruing
 * inventory under a cabinet nobody is looking at.
 *
 * This checks that an id is *legal*, not that it belongs to this laptop:
 * a valid id for a different cabinet passes. Opening the wrong cabinet's
 * inventory is a Firestore-rules concern, not something the client can
 * settle. The physical risk is bounded, though -- sendRelay() no-ops without
 * the cabinet's HID device attached (see ContainersService), so a wrong id
 * corrupts records rather than dispensing medication.
 */
export const knightmedsInstanceGuard: CanActivateFn = async () => {
  const router = inject(Router);
  const authStore = inject(AuthStore);
  const instancesService = inject(KnightmedsInstancesService);
  // Read the signal as an observable while still in an injection context --
  // after the first await below, inject() and toObservable() are unavailable.
  const isLoggedIn$ = toObservable(authStore.isLoggedIn);

  const candidateId = getPendingKnightmedsInstanceId() || tryResolveKnightmedsInstanceId();
  if (!candidateId) {
    return router.createUrlTree([PATH_INVALID_CABINET], {
      queryParams: { reason: 'missing' satisfies InvalidCabinetReason },
    });
  }
  if (candidateId === validatedInstanceId) {
    return true;
  }

  // The lookup below is subject to Firestore security rules, so it can only
  // run once auth has settled. isLoggedIn starts null while the stored
  // session is rehydrated; waiting for a non-null value avoids reading as an
  // anonymous user and misreporting a real cabinet as unverifiable.
  const isLoggedIn = await firstValueFrom(
    isLoggedIn$.pipe(filter((loggedIn): loggedIn is boolean => loggedIn !== null))
  );
  if (!isLoggedIn) {
    return router.createUrlTree([PATH_LOGIN]);
  }

  let exists: boolean;
  try {
    exists = await instancesService.exists(candidateId);
  } catch {
    // Offline, or rules refused the read. Either way this is not the user
    // mistyping a cabinet name, so say so rather than blaming the id.
    return router.createUrlTree([PATH_INVALID_CABINET], {
      queryParams: {
        cabinet: candidateId,
        reason: 'unverifiable' satisfies InvalidCabinetReason,
      },
    });
  }

  if (!exists) {
    return router.createUrlTree([PATH_INVALID_CABINET], {
      queryParams: {
        cabinet: candidateId,
        reason: 'unknown' satisfies InvalidCabinetReason,
      },
    });
  }

  // Only now is it safe to write the id to this laptop for good.
  commitKnightmedsInstanceId(candidateId);
  validatedInstanceId = candidateId;
  return true;
};
